/**
 * Dismiss and snooze an asked question, and hand a held request back (ADR 0056). Through the hub
 * module 'asks' (and approvals for a hand-back); in demo mode they change the demo data in place.
 * The hub's SSE update replaces state.ext.asks, so every surface follows at once.
 */
import { asksStateOf } from '@orgus/protocol';
import type { ApprovalRequest, ApprovalsState, AskSetAside, AsksState, NeedsYouItem } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

const HOUR = 3_600_000;

/** When a snooze ends: in an hour, tomorrow at 09:00 local time ("tomorrow morning"), or when the session moves (null). */
export type SnoozeChoice = 'hour' | 'morning' | 'moves';

export function snoozeUntil(choice: SnoozeChoice, now: number): number | null {
  if (choice === 'hour') return now + HOUR;
  if (choice === 'moves') return null;
  const d = new Date(now);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 9, 0, 0, 0).getTime();
}

export const SNOOZE_LABEL: Record<SnoozeChoice, string> = { hour: 'For 1 hour', morning: 'Until tomorrow morning', moves: 'Until the session moves' };

type Ask = Pick<NeedsYouItem, 'askId' | 'sessionId' | 'reason'>;

function mockState(): AsksState | null {
  const st = useOrgus.getState();
  return st.mode === 'mock' ? asksStateOf(st.ext) : null;
}

function setAsks(data: AsksState): void {
  useOrgus.getState().applyExt({ ext: 'asks', type: 'state', data });
}

function mockPut(ask: Ask, kind: AskSetAside['kind'], until: number | null): AskSetAside {
  const cur = mockState()!;
  const x: AskSetAside = { askId: ask.askId, sessionId: ask.sessionId ?? '', kind, until, by: 'owner', from: 'this browser', at: Date.now(), question: ask.reason || null };
  setAsks({ setAside: [...cur.setAside.filter((a) => a.askId !== ask.askId), x] });
  return x;
}


export async function dismissAsk(ask: Ask): Promise<AskSetAside> {
  if (mockState()) return mockPut(ask, 'dismiss', null);
  const r = await api<{ setAside: AskSetAside; state: AsksState }>(`/api/asks/${encodeURIComponent(ask.askId)}/dismiss`, { method: 'POST', body: { sessionId: ask.sessionId } });
  setAsks(r.state);
  return r.setAside;
}

export async function snoozeAsk(ask: Ask, until: number | null): Promise<AskSetAside> {
  if (mockState()) return mockPut(ask, 'snooze', until);
  const r = await api<{ setAside: AskSetAside; state: AsksState }>(`/api/asks/${encodeURIComponent(ask.askId)}/snooze`, { method: 'POST', body: { sessionId: ask.sessionId, until } });
  setAsks(r.state);
  return r.setAside;
}

export async function undoAsk(askId: string): Promise<void> {
  const cur = mockState();
  if (cur) return setAsks({ setAside: cur.setAside.filter((a) => a.askId !== askId) });
  const r = await api<{ state: AsksState }>(`/api/asks/${encodeURIComponent(askId)}/undo`, { method: 'POST', body: {} });
  setAsks(r.state);
}

/** "Dismiss asked questions older than a day": only asked questions; the hub refuses the lot if one is held. */
export async function dismissAsks(asks: readonly Ask[]): Promise<number> {
  const cur = mockState();
  if (cur) {
    for (const a of asks) mockPut(a, 'dismiss', null);
    return asks.length;
  }
  const r = await api<{ dismissed: number; state: AsksState }>('/api/asks/dismiss', { method: 'POST', body: { items: asks.map((a) => ({ askId: a.askId, sessionId: a.sessionId })) } });
  setAsks(r.state);
  return r.dismissed;
}

/** Hand a controlled session's held request back to its own dialog (approvals). */
export async function handBackApproval(id: string): Promise<ApprovalRequest> {
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState | undefined;
  if (st.mode === 'mock' && cur) {
    const p = cur.pending.find((x) => x.id === id);
    if (!p) throw new Error('That request was already answered.');
    const done: ApprovalRequest = { ...p, state: 'expired', decidedBy: 'owner', decidedAt: Date.now(), decidedFrom: 'this browser', note: 'Handed back: Claude Code asks in the session.', handedBack: true };
    st.applyExt({ ext: 'approvals', type: 'state', data: { ...cur, pending: cur.pending.filter((x) => x.id !== id), recent: [done, ...cur.recent] } });
    return done;
  }
  const r = await api<{ approval: ApprovalRequest }>(`/api/approvals/${encodeURIComponent(id)}/hand-back`, { method: 'POST', body: {} });
  return r.approval;
}
